// Core imports
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Component imports
import { Row, Col, Drawer, Button, Modal, Typography } from 'antd';
import { RiMenuFill, RiCloseFill, RiBookmarkLine, RiErrorWarningLine } from 'react-icons/ri';
import NdeMenu from './NdeMenu';
import BasicInformationTab from './BasicInformationTab';
import AdvancedInformationTab from './AdvancedInformationTab';
import DeathInformationTab from './DeathInformationTab';
import PersonalInformationTab from './PersonalInformationTab';

// Misc imports
import { NAV_NDES, NAV_NDES_EDIT, NAV_RELATIVE_NDES_ADVANCED_DETAILS, NAV_RELATIVE_NDES_BASIC_DETAILS, NAV_RELATIVE_NDES_DEATH_DETAILS, NAV_RELATIVE_NDES_PERSONAL_DETAILS } from '../../constants';
import { deleteNde, fetchNdeByUuid, updateNde } from '../../store/ndes/ndesThunks';
import replaceUrlParams from '../../utils/misc/replaceUrlParams';

const NAV_BASIC_DETAILS = NAV_NDES_EDIT + NAV_RELATIVE_NDES_BASIC_DETAILS;
const NAV_DEATH_DETAILS = NAV_NDES_EDIT + NAV_RELATIVE_NDES_DEATH_DETAILS;
const NAV_ADVANCED_DETAILS = NAV_NDES_EDIT + NAV_RELATIVE_NDES_ADVANCED_DETAILS;
const NAV_PERSONAL_DETAILS = NAV_NDES_EDIT + NAV_RELATIVE_NDES_PERSONAL_DETAILS;

const NdeEditForm = (props) => {
  const { initialValues } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ndeUuid } = useParams();

  const [visible, setVisible] = useState(false);

  // TODO: Show prompt for unsaved changes

  const onSubmitSuccess = async (values) => {
    const isSaveSuccessful = await dispatch(updateNde(ndeUuid, values));

    if (isSaveSuccessful) {
      // Fetch latest data again to be sure
      dispatch(fetchNdeByUuid(ndeUuid));
    }
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const onDeleteNde = () => {
    Modal.confirm({
      title: 'Delete NDE',
      content: 'Are you sure you want to permanently delete this NDE? This action cannot be undone.',
      okText: 'Delete',
      okType: 'primary',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      cancelButtonProps: { danger: true, type: 'default' },
      centered: true,
      autoFocusButton: 'cancel',
      maskClosable: true,
      keyboard: true,
      icon: <RiErrorWarningLine size={24} style={{ color: 'rgb(250, 173, 20)' }} />,
      onOk: async () => {
        const isDeleteSuccessful = await dispatch(deleteNde(ndeUuid));
        if (isDeleteSuccessful) {
          navigate(NAV_NDES, { replace: true });
        }
      }
    });
  };

  return (
    <Row gutter={[32, 32]} className="mb-32">
      <Drawer
        className="nde-mobile-menu"
        placement="left"
        closable={true}
        onClose={onClose}
        visible={visible}
        closeIcon={
          <RiCloseFill
            className="remix-icon text-color-black-80"
            size={24}
          />
        }
      >
        <NdeMenu
          title="Edit NDE"
          subtitle="Edit NDE"
          ndeUuid={ndeUuid}
          mode="edit"
          onCloseDrawer={onClose}
          showFooterImage={false}
        />
      </Drawer>

      <Col span={24}>
        <div
          style={{
            background: 'rgb(239, 239, 255)',
            border: '1px solid rgb(191, 193, 255)',
            borderLeft: '4px solid rgb(145, 149, 252)',
            borderRadius: 8,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 16
          }}
        >
          <RiBookmarkLine size={22} style={{ color: 'rgb(145, 149, 252)', flexShrink: 0, marginTop: 3 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 15, color: 'rgb(100, 104, 210)' }}>
              Save this link: it's the only way to edit your NDE later!
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 14, color: '#666', lineHeight: 1.5 }}>
              Bookmark it or copy it somewhere safe. Do not share it with anyone.
            </p>
            <Typography.Text copyable style={{ fontSize: 14, color: 'rgb(100, 104, 210)', wordBreak: 'break-all', textDecoration: 'underline', cursor: 'pointer' }}>
              {`${window.location.origin}${replaceUrlParams(NAV_NDES_EDIT + NAV_RELATIVE_NDES_BASIC_DETAILS, { ndeUuid })}`}
            </Typography.Text>
          </div>
        </div>

        <Row className="nde-mobile-menu-btn bg-color-black-0 bg-color-dark-100 border-radius elevation py-12 px-sm-8 px-24 mb-16">
          <Button
            className="p-0"
            type="text"
            icon={
              <RiMenuFill
                size={24}
                className="remix-icon text-color-black-80 text-color-dark-30"
              />
            }
            onClick={showDrawer}
          />
        </Row>

        <Row className="bg-color-black-0 bg-color-dark-100 border-radius elevation">
          <NdeMenu
            title="Edit NDE"
            subtitle="Edit NDE"
            ndeUuid={ndeUuid}
            mode="edit"
            showFooterImage
          />

          <Col
            flex="1 1"
            className="pl-sm-16 pl-32 py-sm-24 py-32 pb-24 px-sm-24 px-32 overflow-hidden"
          >
            <Routes>
              <Route
                path={replaceUrlParams(NAV_BASIC_DETAILS.replace(NAV_NDES_EDIT, ''), { ndeUuid: ndeUuid })} // Only keep the relative part
                element={
                  <BasicInformationTab
                    wrapperCol={{ span: 24 }}
                    mode="edit"
                    onSubmitSuccess={onSubmitSuccess}
                    onStepChange={() => {}}
                    initialValues={initialValues}
                    onDelete={onDeleteNde}
                  />
                }
              />

              <Route
                path={replaceUrlParams(NAV_DEATH_DETAILS.replace(NAV_NDES_EDIT, ''), { ndeUuid: ndeUuid })} // Only keep the relative part
                element={
                  <DeathInformationTab
                    wrapperCol={{ span: 24 }}
                    mode="edit"
                    onSubmitSuccess={onSubmitSuccess}
                    onStepChange={() => {}}
                    initialValues={initialValues}
                    onDelete={onDeleteNde}
                  />
                }
              />

              <Route
                path={replaceUrlParams(NAV_ADVANCED_DETAILS.replace(NAV_NDES_EDIT, ''), { ndeUuid: ndeUuid })} // Only keep the relative part
                element={
                  <AdvancedInformationTab
                    wrapperCol={{ span: 24 }}
                    mode="edit"
                    onSubmitSuccess={onSubmitSuccess}
                    onStepChange={() => {}}
                    initialValues={initialValues}
                    onDelete={onDeleteNde}
                  />
                }
              />

              <Route
                path={replaceUrlParams(NAV_PERSONAL_DETAILS.replace(NAV_NDES_EDIT, ''), { ndeUuid: ndeUuid })} // Only keep the relative part
                element={
                  <PersonalInformationTab
                    wrapperCol={{ span: 24 }}
                    mode="edit"
                    onSubmitSuccess={onSubmitSuccess}
                    onStepChange={() => {}}
                    initialValues={initialValues}
                    onDelete={onDeleteNde}
                  />
                }
              />
            </Routes>
          </Col>
        </Row>
      </Col>

    </Row>
  );
};

NdeEditForm.propTypes = {
  initialValues: PropTypes.object
};

export default NdeEditForm;
